import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import ArticleIcon from "@mui/icons-material/Article";
import WorkIcon from "@mui/icons-material/Work";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import HomeNavbar from "../components/home-nav-bar";

function EditorialBoardPage() {
  const [boardMembers, setBoardMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    axios
      .get("https://api.wajls.com/client/v1/fetch_editorial_board.php")
      .then((res) => {
        setBoardMembers(res.data);
        setFilteredMembers(res.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = boardMembers.filter((member) =>
      member.personal_information?.name?.toLowerCase().includes(query)
    );
    setFilteredMembers(filtered);
  }, [searchQuery, boardMembers]);

  const getPositionStyles = (position) => {
    switch (position) {
      case "Editor-in-Chief":
        return { bg: "#e3f2fd", color: "#1976d2", icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
      case "Associate Editor":
        return { bg: "#f3e5f5", color: "#7b1fa2" };
      case "Editorial Assistant":
        return { bg: "#fff3e0", color: "#ef6c00" };
      default:
        return { bg: "#e8f5e9", color: "#2e7d32" };
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <HomeNavbar />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "#1a1a1a" }}>
            Editorial Board
          </Typography>
        </Box>

        {/* <TextField
          fullWidth
          placeholder="Search by name"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            mb: 4,
            backgroundColor: "white",
            borderRadius: 2,
            maxWidth: 600,
            mx: "auto",
            display: "block",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        /> */}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : filteredMembers.length === 0 ? (
          <Box
            sx={{
              backgroundColor: "white",
              p: 4,
              borderRadius: 2,
              textAlign: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <Typography>No editorial board members found.</Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {filteredMembers.map((member, idx) => {
              const personal = member.personal_information;
              const academic = member.academic_background;
              const publication = member.publication_and_editorial_experience;
              const availability = member.availability_for;
              const position = member.editorial_position || "Editorial Member";
              const posStyle = getPositionStyles(member.editorial_position);

              return (
                <Box
                  key={idx}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 2,
                    p: 3,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    gap={3}
                    sx={{ flexDirection: { xs: "column", md: "row" } }}
                  >
                    {/* Avatar */}
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      gap={2}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      <Avatar
                        src={personal.profile_image}
                        alt={personal.name}
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          border: "3px solid #f0f0f0",
                        }}
                      />

                      {/* Name + Position (mobile) */}
                          <Box
                            flex={1}
                            minWidth={0}
                            sx={{
                              display: { xs: "block", md: "none" },
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: "#1a1a1a", mb: 0.5 }}
                            >
                              {personal.name}
                            </Typography>

                            <Chip
                              label={position}
                              size="small"
                              sx={{
                                backgroundColor: posStyle.bg,
                                color: posStyle.color,
                                fontWeight: 600,
                                borderRadius: "8px",
                                height: 24,
                                mb: 0.5,
                              }}
                              icon={posStyle.icon}
                            />

                            <Typography variant="body1" color="text.secondary">
                              {personal.title}
                            </Typography>
                          </Box>


                    </Box>

                    {/* Main Content */}
                    <Box flex={1}>
                      {/* Name + Position (desktop) */}
                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: "#1a1a1a" }}
                        >
                          {personal.name}
                        </Typography>
                        <Chip
                          label={position}
                          size="small"
                          sx={{
                            backgroundColor: posStyle.bg,
                            color: posStyle.color,
                            fontWeight: 600,
                            borderRadius: "8px",
                            height: 24,
                          }}
                          icon={posStyle.icon}
                        />
                      </Box>

                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: { xs: "none", md: "block" },
                        }}
                      >
                        {personal.title}
                      </Typography>

                      {/* Contact Info */}
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <BusinessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                          <Typography variant="body2" color="text.secondary">
                            {personal.affiliation}{" "}
                            {personal.department && `• ${personal.department}`}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmailIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ wordBreak: "break-word" }}
                            >
                              {personal.email}
                            </Typography>
                          </Box>

                          {personal.phone && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <PhoneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                              <Typography variant="body2" color="text.secondary">
                                {personal.phone}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>

                      {/* Academic Background */}
                      {academic && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1, color: "#1a1a1a" }}
                          >
                            Academic Background
                          </Typography>
                          <Stack spacing={0.5}>
                            {academic.highest_degree && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <SchoolIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                <Typography variant="body2" color="text.secondary">
                                  {academic.highest_degree}
                                </Typography>
                              </Box>
                            )}
                            {academic.years_of_experience && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <WorkIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                <Typography variant="body2" color="text.secondary">
                                  {academic.years_of_experience} years of experience
                                </Typography>
                              </Box>
                            )}
                            {academic.areas_of_expertise && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                              >
                                <strong>Areas of Expertise:</strong>{" "}
                                {academic.areas_of_expertise}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      )}

                      {/* Publications */}
                      {publication && (
                        <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                          {publication.number_of_papers_published && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                backgroundColor: "#f8f9fa",
                                px: 2,
                                py: 1,
                                borderRadius: 1,
                              }}
                            >
                              <ArticleIcon sx={{ fontSize: 18, color: "primary.main" }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {publication.number_of_papers_published} papers published
                              </Typography>
                            </Box>
                          )}

                          {/* {publication.editorial_experience && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                backgroundColor: "#f0f7ff",
                                px: 2,
                                py: 1,
                                borderRadius: 1,
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {publication.editorial_experience}
                              </Typography>
                            </Box>
                          )} */}
                          
                        </Box>
                      )}

                      {/* Availability */}
                      {availability && availability.length > 0 && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1, color: "#1a1a1a" }}
                          >
                            Available For
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {availability.map((item, i) => (
                              <Chip
                                key={i}
                                label={item}
                                size="small"
                                sx={{
                                  backgroundColor: "#e8f5e9",
                                  color: "#2e7d32",
                                  fontWeight: 500,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Container>
    </Box>
  );
}

export default EditorialBoardPage;
